import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { C, t } from '../../../shared/constants/dashboardConstants';
import MiniCalendar from '../../Talent/Dashboard/components/MiniCalendar';
import ProducerContentContainer from '../layout/ProducerContentContainer';
import {
  getProducerDashboardSummary,
  getProducerProjects,
  getProducerRecentApplications,
  getProducerSchedules,
} from '../../../api/producerDashboardApi';
import { getScheduleAccentBackground, normalizeScheduleColor } from '../../../shared/schedule/scheduleColors';
import EventDetailModal from '../../../shared/schedule/EventDetailModal';
import DayEventsModal from '../../../shared/schedule/DayEventsModal';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';
import { AdSideBanner } from '../../../shared/components/AdBanner';
import { useRotatingAd } from '../../../shared/hooks/useAds';


const PROJECT_STATUS = {
  CASTING:       { label: '캐스팅 중', color: C.point },
  IN_PRODUCTION: { label: '제작중',   color: '#A8E6CF' },
  PRODUCTION:    { label: '제작중',   color: '#A8E6CF' },
  PLANNING:      { label: '기획중',   color: C.g200 },
  COMPLETED:     { label: '마감',     color: '#FFE0E0' },
};

function dday(dateStr) {
  if (!dateStr) return '—';
  const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  if (diff < 0) return '마감';
  if (diff === 0) return 'D-Day';
  return `D-${diff}`;
}

function ddayColor(badge) {
  if (/^D-(Day|[1-3])$/.test(badge)) return { color: '#EF4444', bg: '#FEE2E2' };
  if (/^D-[1-9]$/.test(badge)) return { color: '#FF7A00', bg: '#FFF0E5' };
  return { color: C.g400, bg: C.g200 };
}

function relativeTime(isoStr) {
  if (!isoStr) return '';
  const diff = Math.floor((Date.now() - new Date(isoStr)) / 60000);
  if (diff < 60) return `${diff}분 전`;
  if (diff < 1440) return `${Math.floor(diff / 60)}시간 전`;
  return `${Math.floor(diff / 1440)}일 전`;
}

function toDateOnly(value) {
  if (!value) return '';
  return String(value).slice(0, 10);
}

function toDateStr(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function formatScheduleDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16).replace('T', ' ');
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function DonutChart({ segments = [], size = 84, sw = 13, trackColor = C.g200 }) {
  const r = (size - sw) / 2;
  const circ = 2 * Math.PI * r;
  let acc = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={trackColor} strokeWidth={sw} />
      {segments.map((segment, index) => {
        const pct = Math.max(0, Math.min(1, segment.pct || 0));
        const dash = pct * circ;
        const dashOffset = -acc * circ;
        acc += pct;
        return (
          <circle
            key={`${segment.color}-${index}`}
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={segment.color}
            strokeWidth={sw}
            strokeDasharray={`${dash} ${circ - dash}`}
            strokeDashoffset={dashOffset}
            strokeLinecap="butt"
          />
        );
      })}
    </svg>
  );
}

function AvatarCircle({ size = 36 }) {
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: C.g200, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="8" r="4" stroke={C.g400} strokeWidth="1.5" />
        <path d="M5 20c0-3.9 3.1-7 7-7s7 3.1 7 7" stroke={C.g400} strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    </div>
  );
}

function SectionHeader({ title, onViewAll }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
      <span style={t('18px', 600, C.bk)}>{title}</span>
      <button onClick={onViewAll} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', padding: 0 }}>
        <span style={{ color: C.bk, fontFamily: 'Pretendard, sans-serif', fontSize: '16px', fontStyle: 'normal', fontWeight: 400, lineHeight: '140%', letterSpacing: '-0.64px' }}>전체 보기</span>
        <img
          src="/assets/images/here.svg"
          alt=""
          aria-hidden="true"
          style={{ width: '10.717px', height: '10.55px', display: 'block', flexShrink: 0 }}
        />
      </button>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div style={{ padding: '30px', textAlign: 'center', border: `1px solid ${C.g200}`, borderRadius: '12px', background: C.white }}>
      <span style={t('14px', 400, C.sub)}>{message}</span>
    </div>
  );
}

const STAT_COLORS = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EC4899'];

function buildDonutSegments(items, valueKey) {
  const values = items.map((item) => Math.max(0, item?.[valueKey] || 0));
  const total = values.reduce((sum, value) => sum + value, 0);
  if (total <= 0) return [{ pct: 1, color: C.g200 }];
  return values
    .map((value, index) => ({ pct: value / total, color: STAT_COLORS[index % STAT_COLORS.length] }))
    .filter((segment) => segment.pct > 0);
}

export default function ProducerDashboardPage() {
  const navigate = useNavigate();
  const { ad: dashboardAd } = useRotatingAd('producer-dashboard');

  const [loading, setLoading]       = useState(true);
  const [summary, setSummary]       = useState(null);
  const [projects, setProjects]     = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [upcoming, setUpcoming]     = useState([]);
  const [calendarMarked, setCalendarMarked] = useState([]);
  const [detailEvent, setDetailEvent] = useState(null);
  const [dayModal, setDayModal] = useState(null);

  const fetchCalendarSchedules = useCallback((year, month) => {
    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0);
    const start = new Date(monthStart);
    start.setDate(monthStart.getDate() - monthStart.getDay());
    const end = new Date(monthEnd);
    end.setDate(monthEnd.getDate() + (6 - monthEnd.getDay()));
    getProducerSchedules(toDateStr(start), toDateStr(end))
      .then(list => {
        const arr = Array.isArray(list) ? list : (list?.content ?? []);
        const dates = arr
          .map((s) => ({
            ...s,
            date: toDateOnly(s.eventDate || s.date || s.startAt || s.scheduledAt),
            color: normalizeScheduleColor(s.color || s.eventColor),
          }))
          .filter((item) => item.date);
        setCalendarMarked(dates);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const now = new Date();
    const todayStr = toDateStr(now);
    const future = new Date(now);
    future.setDate(future.getDate() + 30);
    const endStr = toDateStr(future);

    fetchCalendarSchedules(now.getFullYear(), now.getMonth());

    Promise.all([
      getProducerDashboardSummary().catch(() => null),
      getProducerProjects().catch(() => []),
      getProducerRecentApplications().catch(() => []),
      getProducerSchedules(todayStr, endStr).catch(() => []),
    ])
      .then(([summaryData, projectsData, applicantsData, schedules]) => {
        setSummary(summaryData);
        setAllProjects(projectsData);
        setProjects(projectsData.filter(p => p.rawStatus === 'OPEN').slice(0, 3));
        setApplicants(applicantsData.slice(0, 5));

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const parseDate = (s) => new Date(s.startAt || s.scheduledAt || 0);
        setUpcoming(schedules.filter(s => parseDate(s) >= today).slice(0, 5));

        setLoading(false);
      });
  }, [fetchCalendarSchedules]);

  const s = summary || {};

  return (
    <>
    <ProducerContentContainer>
      <div style={{ padding: 'clamp(28px, 6vw, 50px) clamp(16px, 4vw, 40px) 40px' }}>
        {/* Header */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '30px' }}>
          <h1 style={{ ...t('36px', 600, C.bk), margin: 0 }}>제작사 대시보드</h1>
          <span style={t('16px', 400, C.sub)}>공고 진행 상황과 지원자를 한눈에 파악하세요.</span>
        </div>
      </div>

      <div style={{ padding: '0 clamp(16px, 4vw, 40px) 60px' }}>
          {/* Stats Row */}
          {/* 좁아지면 칸을 짜부러뜨리지 말고 줄을 바꾼다 — 안 그러면 글자가 한 자씩 끊긴다 */}
          <div style={{ background: C.white, border: `1px solid ${C.g200}`, borderRadius: '16px', display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', marginBottom: '30px', overflow: 'hidden' }}>

            {/* 공고 수 */}
            {/* 기준폭(220px)으로 줄바꿈을 결정하고, 한 줄에 혼자 남으면 minWidth: 0으로 더 줄어 글자가 말줄임표로 잘린다 */}
            <div style={{ flex: '1 1 220px', minWidth: 0, padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8L14 2z" stroke={C.sub} strokeWidth="1.5" strokeLinecap="round"/></svg>
                <span style={{ ...t('13px', 500, C.sub), whiteSpace: 'nowrap' }}>공고 수</span>
              </div>
              <span style={{ ...t('32px', 700, C.bk), lineHeight: 1 }}>
                {loading ? '—' : allProjects.filter(p => p.rawStatus !== 'DRAFT').length} <span style={{ fontSize: '18px', fontWeight: 500 }}>개</span>
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                {[['진행 중', allProjects.filter(p => p.rawStatus === 'OPEN').length], ['마감', allProjects.filter(p => p.rawStatus === 'CLOSED').length], ['지원자', allProjects.filter(p => p.rawStatus !== 'DRAFT').reduce((sum, p) => sum + (p.applicantCount || 0), 0)]].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ ...t('13px', 400, C.sub), whiteSpace: 'nowrap' }}>{k}</span>
                    <span style={{ ...t('13px', 500, C.bk), whiteSpace: 'nowrap' }}>{loading ? '—' : (v ?? 0)}{k === '지원자' ? '명' : '개'}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 공고별 조회수 */}
            <div style={{ flex: '1.5 1 280px', minWidth: 0, padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><ellipse cx="12" cy="12" rx="9" ry="6" stroke={C.sub} strokeWidth="1.5"/><circle cx="12" cy="12" r="2.5" stroke={C.sub} strokeWidth="1.5"/></svg>
                <span style={{ ...t('13px', 500, C.sub), whiteSpace: 'nowrap' }}>공고별 조회수</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', minWidth: 0 }}>
                {(() => {
                  const sorted = [...allProjects].sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0)).slice(0, 5);
                  return <DonutChart segments={buildDonutSegments(sorted, 'viewCount')} />;
                })()}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0, flex: 1 }}>
                  <span style={{ ...t('28px', 700, C.bk), lineHeight: 1 }}>
                    {loading ? '—' : (allProjects.reduce((sum, p) => sum + (p.viewCount || 0), 0)).toLocaleString()}
                  </span>
                  <span style={t('12px', 400, C.sub)}>총 조회수</span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px', minWidth: 0 }}>
                    {loading ? null : [...allProjects].sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0)).slice(0, 5).map((p, i) => (
                      <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: STAT_COLORS[i % STAT_COLORS.length], flexShrink: 0 }} />
                        <span style={{ ...t('12px', 600, C.bk), flexShrink: 0 }}>{p.viewCount ?? 0}회</span>
                        <span style={{ ...t('12px', 400, C.sub), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                          {p.title}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* 지원 현황 */}
            <div style={{ flex: '1.5 1 280px', minWidth: 0, padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><ellipse cx="12" cy="12" rx="9" ry="6" stroke={C.sub} strokeWidth="1.5"/><circle cx="12" cy="12" r="2.5" stroke={C.sub} strokeWidth="1.5"/></svg>
                <span style={{ ...t('13px', 500, C.sub), whiteSpace: 'nowrap' }}>지원 현황</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', minWidth: 0 }}>
                {(() => {
                  const sorted = [...allProjects].sort((a, b) => (b.applicantCount || 0) - (a.applicantCount || 0)).slice(0, 5);
                  return <DonutChart segments={buildDonutSegments(sorted, 'applicantCount')} />;
                })()}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0, flex: 1 }}>
                  <span style={{ ...t('28px', 700, C.bk), lineHeight: 1 }}>
                    {loading ? '—' : allProjects.reduce((sum, p) => sum + (p.applicantCount || 0), 0).toLocaleString()}
                  </span>
                  <span style={t('12px', 400, C.sub)}>총 지원 수</span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px', minWidth: 0 }}>
                    {loading ? null : [...allProjects].sort((a, b) => (b.applicantCount || 0) - (a.applicantCount || 0)).slice(0, 5).map((p, i) => (
                      <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: STAT_COLORS[i % STAT_COLORS.length], flexShrink: 0 }} />
                        <span style={{ ...t('12px', 600, C.bk), flexShrink: 0 }}>{p.applicantCount ?? 0}명</span>
                        <span style={{ ...t('12px', 400, C.sub), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                          {p.title}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* 리뷰 */}
            {/* <div style={{ flex: 1, padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="16" height="16" viewBox="0 0 20 20"><path d="M10 1.5l2.5 5.1 5.6.8-4.1 4 .97 5.6L10 14.3l-4.97 2.7.97-5.6-4.1-4 5.6-.8L10 1.5z" fill={C.point}/></svg>
                <span style={t('13px', 500, C.sub)}>리뷰</span>
              </div>
              <div style={{ display: 'flex', gap: '2px' }}>
                {[1,2,3,4,5].map(i => {
                  const filled = !loading && i <= Math.round(s.avgRating ?? 0);
                  return <svg key={i} width="18" height="18" viewBox="0 0 20 20"><path d="M10 1.5l2.5 5.1 5.6.8-4.1 4 .97 5.6L10 14.3l-4.97 2.7.97-5.6-4.1-4 5.6-.8L10 1.5z" fill={filled ? C.point : C.g200}/></svg>;
                })}
              </div>
              <span style={{ ...t('32px', 700, C.bk), lineHeight: 1 }}>{loading ? '—' : (s.avgRating ?? 0).toFixed(1)}</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={t('13px', 400, C.sub)}>평균 별점</span>
                <span style={t('13px', 500, C.bk)}>리뷰 {loading ? '—' : (s.reviewCount ?? 0)}개</span>
              </div>
            </div> */}

          </div>

        {/* Main content */}

          {/* Row 1: 진행 중인 프로젝트 + 최근 지원자 */}
          <div style={{ display: 'flex', gap: '24px', marginBottom: '32px' }}>

            {/* 진행 중인 프로젝트 */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <SectionHeader title="진행 중인 프로젝트" onViewAll={() => navigate('/producer/projects')} />
              {loading ? (
                <div style={{ padding: '30px', display: 'flex', justifyContent: 'center', border: `1px solid ${C.g200}`, borderRadius: '12px', background: C.white }}><LoadingSpinner size="sm" /></div>
              ) : projects.length === 0 ? (
                <EmptyState message="진행 중인 프로젝트가 없습니다." />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {projects.map((p) => {
                    const statusInfo = PROJECT_STATUS[p.status] || { label: p.status, color: C.g200 };
                    const badge = dday(p.deadline);
                    const { color: dColor } = ddayColor(badge);
                    return (
                      <div key={p.id} style={{ background: C.white, border: `1px solid ${C.g200}`, borderRadius: '12px', padding: '20px 24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', gap: '6px', marginBottom: '10px', flexWrap: 'wrap' }}>
                              {(p.genres || []).map(g => (
                                <span key={g} style={{ padding: '4px 10px', borderRadius: '20px', background: C.point, ...t('12px', 600, C.bk) }}>{g}</span>
                              ))}
                              <span style={{ padding: '4px 10px', borderRadius: '20px', background: statusInfo.color, ...t('12px', 600, C.bk) }}>{statusInfo.label}</span>
                            </div>
                            <div style={{ ...t('18px', 600, C.bk), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</div>
                          </div>
                          <button onClick={() => navigate('/producer/projects', { state: { managingProjectId: p.id, managingProjectTitle: p.title } })} style={{ padding: '10px 24px', borderRadius: '8px', background: C.bk, border: 'none', cursor: 'pointer', ...t('14px', 600, C.white), flexShrink: 0, alignSelf: 'center' }}>관리하기</button>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" stroke={C.sub} strokeWidth="1.5"/><path d="M5 20c0-3.9 3.1-7 7-7s7 3.1 7 7" stroke={C.sub} strokeWidth="1.5" strokeLinecap="round"/></svg>
                            <span style={t('13px', 400, C.sub)}>{p.applicantCount ?? 0}명 지원</span>
                          </div>
                          <span style={t('13px', 500, dColor)}>{badge}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 최근 지원자 */}
            <div style={{ width: '280px', flexShrink: 0 }}>
              <SectionHeader title="최근 지원자" onViewAll={() => navigate('/producer/projects')} />
              {loading ? (
                <div style={{ padding: '30px', display: 'flex', justifyContent: 'center', border: `1px solid ${C.g200}`, borderRadius: '12px', background: C.white }}><LoadingSpinner size="sm" /></div>
              ) : applicants.length === 0 ? (
                <EmptyState message="최근 지원자가 없습니다." />
              ) : (
                <div style={{ background: C.white, border: `1px solid ${C.g200}`, borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {applicants.map((a) => (
                    <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <AvatarCircle />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={t('14px', 600, C.bk)}>{a.actorName}</div>
                        <div style={{ ...t('12px', 400, C.sub), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.castingTitle}</div>
                      </div>
                      <span style={t('12px', 400, C.sub)}>{relativeTime(a.appliedAt)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 광고 배너 */}
            {dashboardAd && (
              <div style={{ width: '240px', flexShrink: 0 }}>
                <AdSideBanner ad={dashboardAd} />
              </div>
            )}

          </div>

          {/* Row 2: 다가오는 일정 + 캘린더 */}
          <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>

            {/* 다가오는 일정 */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <SectionHeader title="다가오는 일정" onViewAll={() => navigate('/producer/schedule')} />
              <div style={{ background: C.white, border: `1px solid ${C.g200}`, borderRadius: '16px', padding: '0 20px' }}>
                {loading ? (
                  <div style={{ padding: '20px 0', display: 'flex', justifyContent: 'center' }}>
                    <LoadingSpinner size="sm" />
                  </div>
                ) : upcoming.length === 0 ? (
                  <div style={{ padding: '20px 0', textAlign: 'center' }}>
                    <span style={t('14px', 400, C.sub)}>다가오는 일정이 없습니다.</span>
                  </div>
                ) : (
                  upcoming.map((item, i) => {
                    const dateStr = item.startAt || item.scheduledAt;
                    const badge = dday(dateStr);
                    const scheduleColor = normalizeScheduleColor(item.color || item.eventColor);
                    const bg = getScheduleAccentBackground(scheduleColor);
                    return (
                      <button key={item.id} type="button" onClick={() => setDetailEvent(item)} style={{ width: '100%', background: 'transparent', border: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderBottom: i < upcoming.length - 1 ? `1px solid ${C.g200}` : 'none', cursor: 'pointer', textAlign: 'left', gap: '16px' }}>
                        <span style={{ ...t('15px', 500, C.bk), minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                          <span style={{ ...t('12px', 400, C.sub), whiteSpace: 'nowrap' }}>
                            {formatScheduleDate(dateStr)} {item.time || ''}
                          </span>
                          <span style={{ background: bg, border: `1px solid ${scheduleColor}`, borderRadius: '6px', padding: '4px 10px', ...t('13px', 600, scheduleColor), flexShrink: 0 }}>{badge}</span>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {/* 캘린더 */}
            <div style={{ width: '280px', flexShrink: 0 }}>
              <SectionHeader title="캘린더" onViewAll={() => navigate('/producer/schedule')} />
              <div style={{ background: C.white, border: `1px solid ${C.g200}`, borderRadius: '16px', padding: '16px' }}>
                <MiniCalendar
                  markedDates={calendarMarked}
                  onMonthChange={(y, m) => fetchCalendarSchedules(y, m)}
                  onDateClick={(date, events) => {
                    if (events.length > 0) setDayModal({ date, events });
                  }}
                />
              </div>
            </div>

          </div>
      </div>
      </ProducerContentContainer>
      {detailEvent && (
        <EventDetailModal
          event={detailEvent}
          onClose={() => setDetailEvent(null)}
        />
      )}
      {dayModal && (
        <DayEventsModal
          date={dayModal.date}
          events={dayModal.events}
          onClose={() => setDayModal(null)}
        />
      )}
    </>
  );
}
